import * as bffenums from '@ps20/bff-models';
import {
  AdresseVorvertrag,
  Berufsrisikogruppe,
  DiagnoseVorvertrag,
  Gegenstand,
  GegeVorvertragsdaten,
  JhpVorvertragsdaten,
  PhVorvertragsdaten,
  RsVorvertragsdaten,
  SpartenType,
  TaasTarifrisikoschluesselGEGE,
  TaasTarifrisikoschluesselJHP,
  TaasTarifrisikoschluesselPH,
  TaasTarifrisikoschluesselRS,
  TaasTarifrisikoschluesselTHH,
  TaasTarifrisikoschluesselTKV,
  TaasTarifrisikoschluesselTKV_V2,
  TaasTarifrisikoschluesselURI,
  TaasTarifrisikoschluesselVHV,
  TaasTarifrisikoschluesselVHVV2,
  TaasTarifrisikoschluesselVWG,
  TaasTarifrisikoschluesselVWGV2,
  ThhHundVorvertragsdaten,
  ThhPferdVorvertragsdaten,
  ThhTier,
  TkvVorvertragsdaten,
  TkvV2Vorvertragsdaten,
  UnfallVorvertragsdaten,
  VertragsVorbelegungdaten,
  VhvV2Vorvertragsdaten,
  VhvVorvertragsdaten,
  VorvertragBaustein,
  Vorvertragsdaten,
  VwgVorvertragsdaten,
  VwgV2Vorvertragsdaten,
  VwgVorvertragsdatenMerkmale,
  ProfiSport,
} from '@ps20/bff-models';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import { App } from './ps20apps';
import { GisDatenVHV, GisDatenVWG } from './gisDaten';
import { Risikoort } from './risikoort';
import { isEqualForRelevantFields } from './immobilie';

export type TaasTarifrisikoschluessel =
  | TaasTarifrisikoschluesselVWG
  | TaasTarifrisikoschluesselVWGV2
  | TaasTarifrisikoschluesselVHV
  | TaasTarifrisikoschluesselVHVV2
  | TaasTarifrisikoschluesselPH
  | TaasTarifrisikoschluesselRS
  | TaasTarifrisikoschluesselTHH
  | TaasTarifrisikoschluesselTKV
  | TaasTarifrisikoschluesselTKV_V2
  | TaasTarifrisikoschluesselJHP
  | TaasTarifrisikoschluesselGEGE
  | TaasTarifrisikoschluesselURI;

export interface Vorbelegungdaten {
  forApp: App;
  angebotsId?: string;
  produktlinienname?: bffenums.Produktlinienname;
  verkaufsangebotKurzname?: string;
  vorvertragBausteinList?: VorvertragBaustein<TaasTarifrisikoschluessel>[];
  preisverzeichnis?: number;
  selbstbeteiligung?: bffenums.Selbstbeteiligung;
  selbstbeteiligungOriginal?: bffenums.Selbstbeteiligung;
  geburtsdatumVn?: string;
  vnAdresse?: VorvertragAdresse;
  vpAdresse?: VorvertragAdresse;
  nachlassRbd?: number;
  nachlassTb?: number;
  nachlaesseFaktor?: number;
  vorteilsrabatt?: number;
  angebotsdatum?: string;
  zuschlaegeFaktor?: number;
  aktionsNachlass?: number;
  erhRisiko?: number;
  manuellerNachlass?: number;
  manuellerZuschlag?: number;
  beginnDatum?: string;
  ablaufDatum?: string;
  wirksamkeitsbeginn?: string;
  aenderungdatum?: string;
  laufzeit?: number;
  vorversicherungVorhanden?: boolean;
  anpassungsFaktor?: number;
  shouldDisableBeginndatum?: boolean;
  tarifbereich?: bffenums.Tarifbereich;
  kurzbezeichnung?: string;
  vorname?: string;
  nachname?: string;
  geburtsdatum?: string;
  beitragsfreiBeibehalten?: boolean;
  urbeginn?: string;
  internerVertragsbezugs?: string[];
  isVnWechsel?: boolean;
}

export interface BasisRisikoort {
  risikoort: VorvertragAdresse;
  wohnflaeche: number;
}

export interface VwgVorbelegungdaten extends Vorbelegungdaten, BasisRisikoort {
  nebengebaeude: boolean;
  hasElementar?: boolean;
  baujahr?: number;
  merkmale?: VwgVorvertragsdatenMerkmale;
  gisDaten?: GisDatenVWG;
  plzFaktorErdb?: number;
  plzFaktorSnd?: number;
  plzFaktorGla?: number;
  plzFaktorHws?: number;
  extremwetterschutzSelbstbeteiligung?: number;
  selbstbehaltUeberschwemmung?: number;
  erdbebenSelbstbeteiligung?: number;
}

export type VwgV2Vorbelegungdaten = VwgVorbelegungdaten;

export type VhvV2Vorbelegungdaten = VhvVorbelegungdaten;

export interface UnfallVorbelegungdaten extends Vorbelegungdaten {
  anrede: string;
  vpAdresse: VorvertragAdresse;
  vorname: string;
  nachname: string;
  geburtsdatum: string;
  kundennummer: string;
  berufsrisikogruppe: Berufsrisikogruppe;
  profiSport: ProfiSport | null;
}

export interface JhpVorbelegungdaten extends Vorbelegungdaten {
  vpAdresse: VorvertragAdresse;
  vorname: string;
  nachname: string;
  geburtsdatum: string;
}

export interface GegeVorbelegungdaten extends Vorbelegungdaten {
  versicherteGegenstaende: Array<Gegenstand>;
  bicycleClaims?: number;
}
export interface VhvVorbelegungdaten extends Vorbelegungdaten, BasisRisikoort {
  familienstand: bffenums.FamilienStand;
  hausart: bffenums.HausArt;
  stockwerk: bffenums.Stockwerk;
  staendigBewohnt: boolean;
  hasElementar?: boolean;
  versicherungssumme?: number;
  wertsachensumme?: number;
  fahrradWert?: bffenums.FahrradWert;
  gisDaten?: GisDatenVHV;
  plzFaktorErdb?: number;
  plzFaktorGla?: number;
  plzFaktorHws?: number;
  selbstbehaltUeberschwemmung?: number;
}

export interface VhvRelevantVorbelegungdaten {
  wohnflaeche?: number;
  verssumme?: number;
  vorvertragAdresse?: VorvertragAdresse;
  wertsachensumme?: number;
  fahrradVorbelegungFehler?: string;
  vorvertragBausteinList?: VorvertragBaustein<TaasTarifrisikoschluessel>[];
  vorvertragForApp?: App;
  produktlinienname?: bffenums.Produktlinienname;
}

export interface PhVorbelegungdaten extends Vorbelegungdaten {
  familienstand: bffenums.FamilienStand;
  anzWohnungen?: number;
  anzVorschaeden?: number;
}

export interface RsGrundstueck {
  deltafaktorUnbebaut?: number;
  plzFaktorUnbebaut?: number;
  instanzIdUnbebaut?: number;
  flurstueckNummer?: string;
  flaeche?: number;
  adresse: AdresseVorvertrag;
}

export interface RsGebaeude {
  anzahlWohneinheiten?: number;
  anzahlGewerbeeinheiten?: number;
  anzahlGaragen?: number;
  bruttoJahresMieteWohnungen?: number;
  bruttoJahresMieteGewerbe?: number;
  bruttoJahresMieteGarage?: number;
  deltafaktorWohnung?: number;
  plzFaktorWohnung?: number;
  deltafaktorGewerbe?: number;
  plzFaktorGewerbe?: number;
  deltafaktorGarage?: number;
  plzFaktorGarage?: number;
  instanzIdGarage?: number;
  instanzIdGewerbe?: number;
  instanzIdWohnung?: number;
  adresse: AdresseVorvertrag;
}

export interface RsVorbelegungdaten extends Vorbelegungdaten {
  familienstand: bffenums.FamilienStand;
  oeffentlicherDienst: boolean;
  selbststaendig: boolean;
  anzahlVorschaeden: number;
  fahrzeugAnzahl?: bffenums.FahrzeugeVersichert;
  fahrzeugArt?: bffenums.FahrzeugArt;
  bebauteGebaeude?: RsGebaeude[];
  unbebauteGrundstuecke?: RsGrundstueck[];
  kfzKennzeichen?: string;
  kfzOnly?: boolean | null;
  komfortSelectedVerkehr?: boolean | null;
  premiumSelectedVerkehr?: boolean | null;
  komfortSelectedBeruf?: boolean | null;
  premiumSelectedBeruf?: boolean | null;
}

export interface ThhHundVorbelegungdaten extends Vorbelegungdaten {
  tierrasse: string[];
  tiere: ThhTier[];
  anzTiere: number;
}

export interface ThhPferdVorbelegungdaten extends Vorbelegungdaten {
  stockmassListe: bffenums.Tiergroesse[];
  tiere: ThhTier[];
  anzTiere: number;
}

export interface TkvVorbelegungdaten extends Vorbelegungdaten {
  plzVN: string;
  plzFaktorOp: number;
  plzFaktorHb: number;
  deltafaktor: number;
  anzVorschaedenOp: number;
  hasHeilbehandlungen: boolean;
  geburtsdatumTier: string;
  tierart: bffenums.Tierart;
  tiername: string;
  einsatzgebiet: bffenums.EinsatzgebieteTier;
  kennzeichnungsArt: bffenums.KennzeichnungsArt;
  kennzeichen: string;
  tierhaltung: bffenums.Tierhaltung;
  tiergeschlecht: bffenums.Tiergeschlecht;
  isKastriert: boolean;
  tierrasse: string;
  sublimit: number;
  diagnose: DiagnoseVorvertrag[];
}

export interface TkvV2Vorbelegungdaten extends Vorbelegungdaten {
  plzVN: string;
  plzFaktorOp: number;
  plzFaktorHb: number;
  deltafaktor: number;
  anzVorschaedenOp: number;
  hasHeilbehandlungen: boolean;
  geburtsdatumTier: string;
  tierart: bffenums.Tierart;
  tiername: string;
  einsatzgebiet: bffenums.EinsatzgebieteTier;
  kennzeichnungsArt: bffenums.KennzeichnungsArt;
  kennzeichen: string;
  tierhaltung: bffenums.Tierhaltung;
  tiergeschlecht: bffenums.Tiergeschlecht;
  tierrasse: string;
  sublimit: number;
  diagnose: DiagnoseVorvertrag[];
  zuschlagRisiko: number;
  originalBreed?: string;
  breedMappingApplied?: boolean;
  breedMappingNotificationRequired?: boolean;
  breedMappingText?: string;
  breedSelectionType?: bffenums.BreedSelectionType;
  hasAssistenzhund?: boolean;
  vorsorgebonusUebernahmeSumme?: number;
}

export interface VorvertragAdresse {
  plz: string;
  ort: string;
  strasse: string;
  hausnummer: string;
  postlandSchluessel: string;
  land?: Land;
  ortsteil: string;
  hausnrzus: string;
  status: bffenums.Status;
}

export interface Land {
  text: string;
  wert: string;
}

export function mapVorbelegungdaten(
  spartenType: SpartenType,
  bffVorbelegungdaten: VertragsVorbelegungdaten,
): Vorbelegungdaten {
  switch (spartenType) {
    case SpartenType.WOHNGEBAEUDE:
      return mapVorbelegungdatenVwg(bffVorbelegungdaten as VwgVorvertragsdaten);
    case SpartenType.WOHNGEBAEUDE_V2:
      return mapVorbelegungdatenVwgV2(bffVorbelegungdaten as VwgV2Vorvertragsdaten);
    case SpartenType.HAUSRAT:
      return mapVorbelegungdatenVhv(bffVorbelegungdaten as VhvVorvertragsdaten);
    case SpartenType.HAUSRAT_V2:
      return mapVorbelegungdatenVhvV2(bffVorbelegungdaten as VhvV2Vorvertragsdaten);
    case SpartenType.PRIVATHAFTPFLICHT:
      return mapVorbelegungdatenPh(bffVorbelegungdaten as PhVorvertragsdaten);
    case SpartenType.RECHTSSCHUTZ:
    case SpartenType.RECHTSSCHUTZ_VERMIETUNG:
      return mapVorbelegungdatenRs(bffVorbelegungdaten as RsVorvertragsdaten, spartenType);
    case SpartenType.TIERHALTERHAFTPFLICHT_HUND:
      return mapVorbelegungdatenThhHund(bffVorbelegungdaten as ThhHundVorvertragsdaten);
    case SpartenType.TIERHALTERHAFTPFLICHT_PFERD:
      return mapVorbelegungdatenThhPferd(bffVorbelegungdaten as ThhPferdVorvertragsdaten);
    case SpartenType.TIERKRANKEN:
      return mapVorbelegungdatenTkv(bffVorbelegungdaten as TkvVorvertragsdaten);
    case SpartenType.TIERKRANKEN_V2:
      return mapVorbelegungdatenTkvV2(bffVorbelegungdaten as TkvV2Vorvertragsdaten);
    case SpartenType.UNFALL:
      return mapVorbelegungsdatenUnfall(bffVorbelegungdaten as UnfallVorvertragsdaten);
    case SpartenType.JAGDHAFTPFLICHT:
      return mapVorbelegungdatenJhp(bffVorbelegungdaten as JhpVorvertragsdaten);
    case SpartenType.GEGENSTANDSSCHUTZ:
      return mapVorbelegungdatenGege(bffVorbelegungdaten as GegeVorvertragsdaten);
    default:
      throw new Error(
        'Vorbelegungdaten von ' + `${spartenType}` + ' kann noch nicht bearbeitet werden',
      );
  }
}

function mapVorbelegungdatenUebergreifend(
  bffVorbelegungsdaten: Vorvertragsdaten<TaasTarifrisikoschluessel>,
  forApp: App,
): Vorbelegungdaten {
  return {
    forApp,
    produktlinienname: bffVorbelegungsdaten.produktlinienname,
    verkaufsangebotKurzname: bffVorbelegungsdaten.verkaufsangebotKurzname,
    vorvertragBausteinList: bffVorbelegungsdaten.vorvertragBausteinList,
    preisverzeichnis: bffVorbelegungsdaten.preisverzeichnis,
    selbstbeteiligung: bffVorbelegungsdaten.selbstbeteiligung,
    selbstbeteiligungOriginal: bffVorbelegungsdaten.selbstbeteiligungOriginal,
    geburtsdatumVn: bffVorbelegungsdaten.geburtsdatumVn,
    vnAdresse: mapVorvertragAdresse(bffVorbelegungsdaten.adresseVorvertrag),
    nachlassRbd: bffVorbelegungsdaten.nachlassRbd,
    nachlassTb: bffVorbelegungsdaten.nachlassTb,
    aktionsNachlass: bffVorbelegungsdaten.aktionsNachlass,
    nachlaesseFaktor: bffVorbelegungsdaten.nachlaesseFaktor,
    angebotsdatum: bffVorbelegungsdaten.angebotsdatum,
    vorteilsrabatt: bffVorbelegungsdaten.vorteilsrabatt,
    zuschlaegeFaktor: bffVorbelegungsdaten.zuschlaegeFaktor,
    beginnDatum: bffVorbelegungsdaten.beginnDatum,
    ablaufDatum: bffVorbelegungsdaten.ablaufDatum,
    wirksamkeitsbeginn: bffVorbelegungsdaten.wirksamkeitsbeginn,
    shouldDisableBeginndatum: bffVorbelegungsdaten.shouldDisableBeginndatum,
    laufzeit: bffVorbelegungsdaten.laufzeit,
    vorversicherungVorhanden: bffVorbelegungsdaten.vorversicherungVorhanden,
    anpassungsFaktor: bffVorbelegungsdaten.anpassungsfaktor,
    manuellerZuschlag: bffVorbelegungsdaten.manuellerZuschlag,
    manuellerNachlass: bffVorbelegungsdaten.manuellerNachlass,
    erhRisiko: bffVorbelegungsdaten.erhRisiko,
    tarifbereich: bffVorbelegungsdaten?.tarifbereich,
    kurzbezeichnung: bffVorbelegungsdaten?.kurzbezeichnung,
    beitragsfreiBeibehalten: bffVorbelegungsdaten?.beitragsfreiBeibehalten,
    urbeginn: bffVorbelegungsdaten.urbeginn,
    internerVertragsbezugs: bffVorbelegungsdaten?.internerVertragsbezugs,
  };
}

function mapVorbelegungdatenVwg(bffVorbelegungdaten: VwgVorvertragsdaten): VwgVorbelegungdaten {
  const vwgVorbelegungdaten: VwgVorbelegungdaten = mapVorbelegungdatenUebergreifend(
    bffVorbelegungdaten,
    App.VWG,
  ) as VwgVorbelegungdaten;
  vwgVorbelegungdaten.risikoort = mapVorvertragAdresse(bffVorbelegungdaten.risikoort);
  vwgVorbelegungdaten.nebengebaeude = bffVorbelegungdaten.nebengebaeude;
  vwgVorbelegungdaten.wohnflaeche = bffVorbelegungdaten.wohnflaeche;
  vwgVorbelegungdaten.baujahr = bffVorbelegungdaten.baujahr;
  vwgVorbelegungdaten.merkmale = bffVorbelegungdaten.merkmale;
  vwgVorbelegungdaten.gisDaten = bffVorbelegungdaten.gisDaten;
  vwgVorbelegungdaten.plzFaktorErdb = bffVorbelegungdaten.plzFaktorErdb;
  vwgVorbelegungdaten.plzFaktorSnd = bffVorbelegungdaten.plzFaktorSnd;
  vwgVorbelegungdaten.hasElementar = bffVorbelegungdaten.hasElementar;
  vwgVorbelegungdaten.plzFaktorHws = bffVorbelegungdaten.plzFaktorHws;
  vwgVorbelegungdaten.plzFaktorGla = bffVorbelegungdaten.plzFaktorGla;
  vwgVorbelegungdaten.selbstbehaltUeberschwemmung = bffVorbelegungdaten.selbstbehaltUeberschwemmung;
  vwgVorbelegungdaten.erdbebenSelbstbeteiligung = bffVorbelegungdaten.selbstbehaltErdbeben;
  return vwgVorbelegungdaten;
}

function mapVorbelegungdatenVwgV2(bffVorbelegungdaten: VwgV2Vorvertragsdaten): VwgV2Vorbelegungdaten {
  const vwgV2Vorbelegungdaten: VwgV2Vorbelegungdaten = mapVorbelegungdatenUebergreifend(
    bffVorbelegungdaten,
    App.VWG_V2,
  ) as VwgV2Vorbelegungdaten;
  vwgV2Vorbelegungdaten.risikoort = mapVorvertragAdresse(bffVorbelegungdaten.risikoort);
  vwgV2Vorbelegungdaten.nebengebaeude = bffVorbelegungdaten.nebengebaeude;
  vwgV2Vorbelegungdaten.wohnflaeche = bffVorbelegungdaten.wohnflaeche;
  vwgV2Vorbelegungdaten.baujahr = bffVorbelegungdaten.baujahr;
  vwgV2Vorbelegungdaten.merkmale = bffVorbelegungdaten.merkmale;
  vwgV2Vorbelegungdaten.gisDaten = bffVorbelegungdaten.gisDaten;
  vwgV2Vorbelegungdaten.plzFaktorErdb = bffVorbelegungdaten.plzFaktorErdb;
  vwgV2Vorbelegungdaten.plzFaktorSnd = bffVorbelegungdaten.plzFaktorSnd;
  vwgV2Vorbelegungdaten.hasElementar = bffVorbelegungdaten.hasElementar;
  vwgV2Vorbelegungdaten.plzFaktorHws = bffVorbelegungdaten.plzFaktorHws;
  vwgV2Vorbelegungdaten.plzFaktorGla = bffVorbelegungdaten.plzFaktorGla;
  vwgV2Vorbelegungdaten.selbstbehaltUeberschwemmung = bffVorbelegungdaten.selbstbehaltUeberschwemmung;
  vwgV2Vorbelegungdaten.erdbebenSelbstbeteiligung = bffVorbelegungdaten.selbstbehaltErdbeben;
  return vwgV2Vorbelegungdaten;
}

function mapVorbelegungdatenVhv(bffVorbelegungdaten: VhvVorvertragsdaten): VhvVorbelegungdaten {
  const vhvVorbelegungdaten: VhvVorbelegungdaten = mapVorbelegungdatenUebergreifend(
    bffVorbelegungdaten,
    App.VHV,
  ) as VhvVorbelegungdaten;
  vhvVorbelegungdaten.risikoort = mapVorvertragAdresse(bffVorbelegungdaten.risikoort);
  vhvVorbelegungdaten.familienstand = bffVorbelegungdaten.familienstand;
  vhvVorbelegungdaten.wohnflaeche = bffVorbelegungdaten.wohnflaeche;
  vhvVorbelegungdaten.hausart = bffVorbelegungdaten.hausart;
  vhvVorbelegungdaten.stockwerk = bffVorbelegungdaten.stockwerk;
  vhvVorbelegungdaten.staendigBewohnt = bffVorbelegungdaten.staendigBewohnt;
  vhvVorbelegungdaten.versicherungssumme = bffVorbelegungdaten.versicherungssumme;
  vhvVorbelegungdaten.wertsachensumme = bffVorbelegungdaten.wertsachensumme;
  vhvVorbelegungdaten.fahrradWert = bffVorbelegungdaten.fahrradWert;
  vhvVorbelegungdaten.gisDaten = bffVorbelegungdaten.gisDaten;
  vhvVorbelegungdaten.plzFaktorErdb = bffVorbelegungdaten.plzFaktorErdb;
  vhvVorbelegungdaten.hasElementar = bffVorbelegungdaten.hasElementar;
  vhvVorbelegungdaten.plzFaktorHws = bffVorbelegungdaten.plzFaktorHws;
  vhvVorbelegungdaten.plzFaktorGla = bffVorbelegungdaten.plzFaktorGla;
  return vhvVorbelegungdaten;
}

function mapVorbelegungdatenVhvV2(bffVorbelegungdaten: VhvV2Vorvertragsdaten): VhvV2Vorbelegungdaten {
  const vhvV2Vorbelegungdaten: VhvV2Vorbelegungdaten = mapVorbelegungdatenUebergreifend(
    bffVorbelegungdaten,
    App.VHV_V2,
  ) as VhvV2Vorbelegungdaten;
  vhvV2Vorbelegungdaten.risikoort = mapVorvertragAdresse(bffVorbelegungdaten.risikoort);
  vhvV2Vorbelegungdaten.familienstand = bffVorbelegungdaten.familienstand;
  vhvV2Vorbelegungdaten.wohnflaeche = bffVorbelegungdaten.wohnflaeche;
  vhvV2Vorbelegungdaten.hausart = bffVorbelegungdaten.hausart;
  vhvV2Vorbelegungdaten.stockwerk = bffVorbelegungdaten.stockwerk;
  vhvV2Vorbelegungdaten.staendigBewohnt = bffVorbelegungdaten.staendigBewohnt;
  vhvV2Vorbelegungdaten.versicherungssumme = bffVorbelegungdaten.versicherungssumme;
  vhvV2Vorbelegungdaten.wertsachensumme = bffVorbelegungdaten.wertsachensumme;
  vhvV2Vorbelegungdaten.fahrradWert = bffVorbelegungdaten.fahrradWert;
  vhvV2Vorbelegungdaten.gisDaten = bffVorbelegungdaten.gisDaten;
  vhvV2Vorbelegungdaten.plzFaktorErdb = bffVorbelegungdaten.plzFaktorErdb;
  vhvV2Vorbelegungdaten.hasElementar = bffVorbelegungdaten.hasElementar;
  vhvV2Vorbelegungdaten.plzFaktorHws = bffVorbelegungdaten.plzFaktorHws;
  vhvV2Vorbelegungdaten.plzFaktorGla = bffVorbelegungdaten.plzFaktorGla;
  vhvV2Vorbelegungdaten.selbstbehaltUeberschwemmung = bffVorbelegungdaten.selbstbehaltUeberschwemmung;
  return vhvV2Vorbelegungdaten;
}

function mapVorbelegungdatenPh(bffVorbelegungdaten: PhVorvertragsdaten): PhVorbelegungdaten {
  const phVorbelegungdaten: PhVorbelegungdaten = mapVorbelegungdatenUebergreifend(
    bffVorbelegungdaten,
    App.PH,
  ) as PhVorbelegungdaten;
  phVorbelegungdaten.familienstand = bffVorbelegungdaten.familienstand;
  phVorbelegungdaten.anzVorschaeden = bffVorbelegungdaten.anzVorschaeden;
  phVorbelegungdaten.anzWohnungen = bffVorbelegungdaten.anzWohnungen;
  return phVorbelegungdaten;
}

function mapVorbelegungdatenThhHund(
  bffVorbelegungdaten: ThhHundVorvertragsdaten,
): ThhHundVorbelegungdaten {
  const thhHundVorbelegungdaten: ThhHundVorbelegungdaten = mapVorbelegungdatenUebergreifend(
    bffVorbelegungdaten,
    App.THH_HUND,
  ) as ThhHundVorbelegungdaten;
  thhHundVorbelegungdaten.anzTiere = bffVorbelegungdaten.anzTiere;
  thhHundVorbelegungdaten.tierrasse = bffVorbelegungdaten.tierrasse;
  thhHundVorbelegungdaten.tiere = bffVorbelegungdaten.tiere;
  return thhHundVorbelegungdaten;
}

function mapVorbelegungdatenThhPferd(
  bffVorbelegungdaten: ThhPferdVorvertragsdaten,
): ThhPferdVorbelegungdaten {
  const thhPferdVorbelegungdaten: ThhPferdVorbelegungdaten = mapVorbelegungdatenUebergreifend(
    bffVorbelegungdaten,
    App.THH_PFERD,
  ) as ThhPferdVorbelegungdaten;
  thhPferdVorbelegungdaten.anzTiere = bffVorbelegungdaten.anzTiere;
  thhPferdVorbelegungdaten.stockmassListe = bffVorbelegungdaten.stockmassListe;
  thhPferdVorbelegungdaten.tiere = bffVorbelegungdaten.tiere;
  return thhPferdVorbelegungdaten;
}

function mapVorbelegungdatenTkv(bffVorbelegungdaten: TkvVorvertragsdaten): TkvVorbelegungdaten {
  const tkvVorbelegungdaten: TkvVorbelegungdaten = mapVorbelegungdatenUebergreifend(
    bffVorbelegungdaten,
    App.TKV,
  ) as TkvVorbelegungdaten;
  tkvVorbelegungdaten.anzVorschaedenOp = bffVorbelegungdaten.anzVorschaedenOp;
  tkvVorbelegungdaten.hasHeilbehandlungen = bffVorbelegungdaten.hasHeilbehandlungen;
  tkvVorbelegungdaten.geburtsdatumTier = bffVorbelegungdaten.geburtsdatumTier;
  tkvVorbelegungdaten.isKastriert = bffVorbelegungdaten.isKastriert;
  tkvVorbelegungdaten.sublimit = bffVorbelegungdaten.sublimit;
  tkvVorbelegungdaten.tierart = bffVorbelegungdaten.tierart;
  tkvVorbelegungdaten.tiername = bffVorbelegungdaten.tiername;
  tkvVorbelegungdaten.einsatzgebiet = bffVorbelegungdaten.einsatzgebiet;
  tkvVorbelegungdaten.kennzeichnungsArt = bffVorbelegungdaten.kennzeichnungsArt;
  tkvVorbelegungdaten.kennzeichen = bffVorbelegungdaten.kennzeichen;
  tkvVorbelegungdaten.tierrasse = bffVorbelegungdaten.tierrasse;
  tkvVorbelegungdaten.tierhaltung = bffVorbelegungdaten.tierhaltung;
  tkvVorbelegungdaten.tiergeschlecht = bffVorbelegungdaten.tiergeschlecht;
  tkvVorbelegungdaten.diagnose = bffVorbelegungdaten.diagnose;

  // Include TKV V2 breed mapping fields when present (for old TIERKRANKEN → TKV V2 migration)
  const bffWithV2Fields = bffVorbelegungdaten as TkvVorvertragsdaten & {
    originalBreed?: string;
    breedMappingApplied?: boolean;
    breedMappingNotificationRequired?: boolean;
    breedMappingText?: string;
    breedSelectionType?: bffenums.BreedSelectionType;
    hasAssistenzhund?: boolean;
  };
  if (bffWithV2Fields.breedSelectionType !== undefined) {
    (tkvVorbelegungdaten as TkvVorbelegungdaten & TkvV2Vorbelegungdaten).originalBreed = bffWithV2Fields.originalBreed;
    (tkvVorbelegungdaten as TkvVorbelegungdaten & TkvV2Vorbelegungdaten).breedMappingApplied = bffWithV2Fields.breedMappingApplied;
    (tkvVorbelegungdaten as TkvVorbelegungdaten & TkvV2Vorbelegungdaten).breedMappingNotificationRequired = bffWithV2Fields.breedMappingNotificationRequired;
    (tkvVorbelegungdaten as TkvVorbelegungdaten & TkvV2Vorbelegungdaten).breedMappingText = bffWithV2Fields.breedMappingText;
    (tkvVorbelegungdaten as TkvVorbelegungdaten & TkvV2Vorbelegungdaten).breedSelectionType = bffWithV2Fields.breedSelectionType;
    (tkvVorbelegungdaten as TkvVorbelegungdaten & TkvV2Vorbelegungdaten).hasAssistenzhund = bffWithV2Fields.hasAssistenzhund;
  }

  return tkvVorbelegungdaten;
}

function mapVorbelegungdatenTkvV2(bffVorbelegungdaten: TkvV2Vorvertragsdaten): TkvV2Vorbelegungdaten {
  const vorbelegungdaten = mapVorbelegungdatenUebergreifend(bffVorbelegungdaten, App.TKV_V2);
  const tkvV2Vorbelegungdaten = vorbelegungdaten as TkvV2Vorbelegungdaten;
  tkvV2Vorbelegungdaten.anzVorschaedenOp = bffVorbelegungdaten.anzVorschaedenOp;
  tkvV2Vorbelegungdaten.hasHeilbehandlungen = bffVorbelegungdaten.hasHeilbehandlungen;
  tkvV2Vorbelegungdaten.geburtsdatumTier = bffVorbelegungdaten.geburtsdatumTier;
  tkvV2Vorbelegungdaten.tierart = bffVorbelegungdaten.tierart;
  tkvV2Vorbelegungdaten.tiername = bffVorbelegungdaten.tiername;
  tkvV2Vorbelegungdaten.einsatzgebiet = bffVorbelegungdaten.einsatzgebiet;
  tkvV2Vorbelegungdaten.kennzeichnungsArt = bffVorbelegungdaten.kennzeichnungsArt;
  tkvV2Vorbelegungdaten.kennzeichen = bffVorbelegungdaten.kennzeichen;
  tkvV2Vorbelegungdaten.tierrasse = bffVorbelegungdaten.tierrasse;
  tkvV2Vorbelegungdaten.tierhaltung = bffVorbelegungdaten.tierhaltung;
  tkvV2Vorbelegungdaten.tiergeschlecht = bffVorbelegungdaten.tiergeschlecht;
  tkvV2Vorbelegungdaten.zuschlagRisiko = bffVorbelegungdaten.zuschlagRisiko;
  tkvV2Vorbelegungdaten.sublimit = bffVorbelegungdaten.sublimit;
  tkvV2Vorbelegungdaten.verkaufsangebotKurzname = bffVorbelegungdaten.verkaufsangebotKurzname;
  tkvV2Vorbelegungdaten.diagnose = bffVorbelegungdaten.diagnose || [];
  tkvV2Vorbelegungdaten.originalBreed = bffVorbelegungdaten.originalBreed;
  tkvV2Vorbelegungdaten.breedMappingApplied = bffVorbelegungdaten.breedMappingApplied;
  tkvV2Vorbelegungdaten.breedMappingNotificationRequired = bffVorbelegungdaten.breedMappingNotificationRequired;
  tkvV2Vorbelegungdaten.breedMappingText = bffVorbelegungdaten.breedMappingText;
  tkvV2Vorbelegungdaten.breedSelectionType = bffVorbelegungdaten.breedSelectionType;
  tkvV2Vorbelegungdaten.hasAssistenzhund = bffVorbelegungdaten.hasAssistenzhund;
  tkvV2Vorbelegungdaten.vorsorgebonusUebernahmeSumme = bffVorbelegungdaten.vorsorgebonusUebernahmeSumme;
  return tkvV2Vorbelegungdaten;
}

function mapVorbelegungdatenRs(
  bffVorbelegungdaten: RsVorvertragsdaten,
  spartenType: SpartenType,
): RsVorbelegungdaten {
  const rsVorbelegungdaten: RsVorbelegungdaten = mapVorbelegungdatenUebergreifend(
    bffVorbelegungdaten,
    spartenType === SpartenType.RECHTSSCHUTZ_VERMIETUNG ? App.RS_VERMIETUNG : App.RS,
  ) as RsVorbelegungdaten;
  rsVorbelegungdaten.familienstand = bffVorbelegungdaten.familienstand;
  rsVorbelegungdaten.oeffentlicherDienst = bffVorbelegungdaten.oeffentlicherDienst;
  rsVorbelegungdaten.selbststaendig = bffVorbelegungdaten.selbststaendig;
  rsVorbelegungdaten.anzahlVorschaeden = bffVorbelegungdaten.anzahlVorschaeden;
  rsVorbelegungdaten.fahrzeugArt = bffVorbelegungdaten.fahrzeugArt;
  rsVorbelegungdaten.fahrzeugAnzahl = bffVorbelegungdaten.fahrzeugAnzahl;
  rsVorbelegungdaten.kfzKennzeichen = bffVorbelegungdaten.kennzeichen;
  rsVorbelegungdaten.kfzOnly = bffVorbelegungdaten.rsCoverageMix?.beruf === null && bffVorbelegungdaten.rsCoverageMix?.verkehr !== null;
  rsVorbelegungdaten.komfortSelectedBeruf = bffVorbelegungdaten.rsCoverageMix?.beruf !== null ? bffVorbelegungdaten.rsCoverageMix?.beruf?.komfort : null;
  rsVorbelegungdaten.premiumSelectedBeruf = bffVorbelegungdaten.rsCoverageMix?.beruf !== null ? bffVorbelegungdaten.rsCoverageMix?.beruf?.premium : null;
  rsVorbelegungdaten.komfortSelectedVerkehr = bffVorbelegungdaten.rsCoverageMix?.verkehr !== null ? bffVorbelegungdaten.rsCoverageMix?.verkehr?.komfort : null;
  rsVorbelegungdaten.premiumSelectedVerkehr = bffVorbelegungdaten.rsCoverageMix?.verkehr !== null ? bffVorbelegungdaten.rsCoverageMix?.verkehr?.premium : null;
  mapVermieterRS(bffVorbelegungdaten, rsVorbelegungdaten);
  return rsVorbelegungdaten;
}

function mapVorbelegungsdatenUnfall(
  bffVorbelegungdaten: UnfallVorvertragsdaten,
): UnfallVorbelegungdaten {
  return {
    ...mapVorbelegungdatenUebergreifend(bffVorbelegungdaten, App.UNF),
    anrede: bffVorbelegungdaten.versicherterPerson?.anrede,
    vorname: bffVorbelegungdaten.versicherterPerson?.vorname,
    nachname: bffVorbelegungdaten.versicherterPerson?.nachname,
    geburtsdatum: bffVorbelegungdaten.versicherterPerson?.geburtsdatum,
    vpAdresse: mapVorvertragAdresse(bffVorbelegungdaten.versicherterPerson?.adresseVorvertrag),
    kundennummer: bffVorbelegungdaten.versicherterPerson?.kundennummer,
    berufsrisikogruppe: bffVorbelegungdaten.versicherterPerson?.berufsrisikogruppe,
    profiSport: bffVorbelegungdaten.versicherterPerson?.profiSport || null,
  };
}
function mapVorbelegungdatenGege(bffVorbelegungsdaten: GegeVorvertragsdaten): GegeVorbelegungdaten {
  const gegeVorbelegungsdaten = mapVorbelegungdatenUebergreifend(
    bffVorbelegungsdaten,
    App.GEGE,
  ) as GegeVorbelegungdaten;
  gegeVorbelegungsdaten.versicherteGegenstaende = bffVorbelegungsdaten.gegenstandList;
  gegeVorbelegungsdaten.bicycleClaims = bffVorbelegungsdaten.bicycleClaims;
  return gegeVorbelegungsdaten;
}
function mapVorbelegungdatenJhp(bffVorbelegungdaten: JhpVorvertragsdaten): JhpVorbelegungdaten {
  const jhpVorbelegungsdaten = mapVorbelegungdatenUebergreifend(
    bffVorbelegungdaten,
    App.JHP,
  ) as JhpVorbelegungdaten;
  jhpVorbelegungsdaten.vorname = bffVorbelegungdaten.versicherterPerson?.vorname;
  jhpVorbelegungsdaten.nachname = bffVorbelegungdaten.versicherterPerson?.nachname;
  jhpVorbelegungsdaten.geburtsdatum = bffVorbelegungdaten.versicherterPerson?.geburtsdatum;
  jhpVorbelegungsdaten.vpAdresse = mapVorvertragAdresse(
    bffVorbelegungdaten.versicherterPerson?.adresseVorvertrag,
  );
  jhpVorbelegungsdaten.aktionsNachlass = bffVorbelegungdaten.aktionsNachlass;
  jhpVorbelegungsdaten.erhRisiko = bffVorbelegungdaten.erhRisiko;
  jhpVorbelegungsdaten.manuellerNachlass = bffVorbelegungdaten.manuellerNachlass;
  jhpVorbelegungsdaten.manuellerZuschlag = bffVorbelegungdaten.manuellerZuschlag;
  return jhpVorbelegungsdaten;
}

function mapVorvertragAdresse(bffAdresse: AdresseVorvertrag): VorvertragAdresse {
  return bffAdresse
    ? {
        plz: bffAdresse.plz,
        ort: bffAdresse.ort,
        strasse: bffAdresse.strasse,
        hausnummer: bffAdresse.hausnummer,
        ortsteil: bffAdresse.ortsteil,
        postlandSchluessel: bffAdresse.postlandSchluessel,
        land: bffAdresse.land,
        hausnrzus: bffAdresse.hausnrzus,
        status: bffAdresse.status,
      }
    : null;
}

export function isEqualAdresse(adresse1: VorvertragAdresse, adresse2: VorvertragAdresse): boolean {
  const adresse1Reduced = omit(adresse1, ['postlandSchluessel', 'ortsteil', 'status']);
  const adresse2Reduced = omit(adresse2, ['postlandSchluessel', 'ortsteil', 'status']);
  return isEqual(adresse1Reduced, adresse2Reduced);
}

function mapVermieterRS(
  bffVorbelegungdaten: RsVorvertragsdaten,
  rsVorbelegungdaten: RsVorbelegungdaten,
): void {
  if (bffVorbelegungdaten.bebauteGebaeude?.length > 0) {
    rsVorbelegungdaten.bebauteGebaeude = [];
    bffVorbelegungdaten.bebauteGebaeude.forEach(value => {
      rsVorbelegungdaten.bebauteGebaeude.push({
        anzahlWohneinheiten: value.anzahlWohneinheiten,
        anzahlGewerbeeinheiten: value.anzahlGewerbeeinheiten,
        anzahlGaragen: value.anzahlGaragen,
        bruttoJahresMieteWohnungen: value.bruttoJahresMieteWohnungen,
        bruttoJahresMieteGewerbe: value.bruttoJahresMieteGewerbe,
        bruttoJahresMieteGarage: value.bruttoJahresMieteGarage,
        deltafaktorGarage: value.deltafaktorGarage,
        deltafaktorGewerbe: value.deltafaktorGewerbe,
        deltafaktorWohnung: value.deltafaktorWohnung,
        plzFaktorGarage: value.plzFaktorGarage,
        plzFaktorGewerbe: value.plzFaktorGewerbe,
        plzFaktorWohnung: value.plzFaktorWohnung,
        instanzIdGarage: value.instanzIdGarage,
        instanzIdGewerbe: value.instanzIdGewerbe,
        instanzIdWohnung: value.instanzIdWohnung,
        adresse: value.adresse,
      });
    });
  }
  if (bffVorbelegungdaten.unbebauteGrundstuecke?.length > 0) {
    rsVorbelegungdaten.unbebauteGrundstuecke = [];
    bffVorbelegungdaten.unbebauteGrundstuecke.forEach(value => {
      rsVorbelegungdaten.unbebauteGrundstuecke.push({
        flurstueckNummer: value.flurstueckNummer,
        flaeche: value.flaeche,
        adresse: value.adresse,
        deltafaktorUnbebaut: value.deltafaktorUnbebaut,
        plzFaktorUnbebaut: value.plzFaktorUnbebaut,
        instanzIdUnbebaut: value.instanzIdUnbebaut,
      });
    });
  }
}

export function getRisikoortIfAllHaveElementarAndHaveSameRisikoort(
  vorbelegungdaten: Array<VwgVorbelegungdaten>,
): Risikoort {
  const risikoort: Array<Risikoort> = [];
  if (vorbelegungdaten?.every(value => value?.hasElementar)) {
    vorbelegungdaten.forEach(value => {
      risikoort.push(getRisikoort(value?.risikoort));
    });
  }
  return risikoort.length > 0 &&
    risikoort.every(value => isEqualForRelevantFields(value, risikoort[0]))
    ? risikoort[0]
    : null;
}

function getRisikoort(vvRisikoort: VorvertragAdresse): Risikoort {
  if (!vvRisikoort) {
    return null;
  }
  return {
    strasse: vvRisikoort.strasse,
    plz: vvRisikoort.plz,
    ort: vvRisikoort.ort,
    hausnummer: vvRisikoort.hausnummer,
  } as Risikoort;
}
